import { Stack, Typography } from "@mui/material";
import { memo, ReactNode } from "react";
import { GradientButton } from "../components/ui";
import { useGetUserQuery, useUpdateFirstLoginMutation } from "../api";
import { toast } from "react-toastify";
import { CustomToast } from "../components/ui/CustomToast";
import { getErrorMessage } from "../utils/getErrorMessage";
import { Navigate } from "react-router-dom";
import { AppRoutes, PlaybookPlanNames } from "../types/enums";
import { CustomLoader } from "../components/ui/CustomLoader";
import {
  AlertInCircleIcon,
  FileIcon,
  RectanglesIcon,
  UpdateIcon,
  ArrowIcon,
  TwoPersonNotFillIcon,
} from "../components/images";

const targetingList = [
  {
    icon: <TwoPersonNotFillIcon />,
    text: "Identifies and maps the complete buying committee for each target account",
  },
  {
    icon: <UpdateIcon width={"32px"} height={"32px"} color="#1C3C6C" />,
    text: "Connects to your CRM seamlessly and enriches it with INFUSE intelligence ",
  },
  {
    icon: <AlertInCircleIcon width={"32px"} height={"32px"} color="#1C3C6C" />,
    text: "Surfaces key gaps - highlighting missing stakeholders in your deals ",
  },
  {
    icon: <RectanglesIcon />,
    text: "Tracks leads engagement, coverage and updates with simple syncing",
  },
  {
    icon: <ArrowIcon width={"32px"} height={"32px"} color="#1C3C6C" />,
    text: "Helps you prioritize accounts and spot momentum shifts",
  },
  {
    icon: <FileIcon color="#1C3C6C" />,
    text: `Equips your teams with strategic ${PlaybookPlanNames.account_plans} and outreach ${PlaybookPlanNames.playbooks}`,
  },
];

const TargetItem = memo(({ text, icon }: { text: string; icon: ReactNode }) => {
  return (
    <Stack flexDirection={"row"} gap={"24px"} alignItems={"center"}>
      <Stack
        sx={{
          flexShrink: 0,
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          backgroundColor: "#D3EAF6",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Stack>
      <Typography variant={"subtitle1"} sx={{ color: "#464646" }}>
        {text}
      </Typography>
    </Stack>
  );
});

const WelcomePage = () => {
  const [updateFirstLogin, { isLoading }] = useUpdateFirstLoginMutation();
  const { data: user, refetch } = useGetUserQuery();

  const updateFirstLoginHandler = () => {
    updateFirstLogin()
      .unwrap()
      .then(() => refetch())
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Something went wrong."}
          />,
        );
      });
  };

  if (!user) {
    return <CustomLoader />;
  }

  if (!user.is_first_login) {
    return <Navigate to={AppRoutes.CAMPAIGNS} replace />;
  }

  return (
    <Stack padding={"72px 24px 0 24px"} alignItems={"center"} flexGrow={1}>
      <Stack gap={"32px"} maxWidth={"696px"}>
        <Typography variant={"h1"} component={"h1"} sx={{ color: "#232323" }}>
          Welcome to{" "}
          <span style={{ color: "#CB6D2B" }}>Buying Committee Radar!</span>
        </Typography>
        <Stack gap={"24px"}>
          <Typography variant={"subtitle1"} sx={{ color: "#464646" }}>
            <span style={{ color: "#1C3C6C", fontWeight: 600 }}>
              Buying Committee Radar
            </span>{" "}
            is your go-to tool for uncovering the real decision-makers behind
            your target deals - powered by industry-leading insights from the{" "}
            <span style={{ color: "#1C3C6C", fontWeight: 600 }}>
              INFUSE Engine.
            </span>
          </Typography>
          <Typography variant={"subtitle1"} sx={{ color: "#464646" }}>
            Here’s how it helps you win more with precision targeting:
          </Typography>
          <Stack gap={"16px"}>
            {targetingList.map(({ text, icon }, index) => (
              <TargetItem key={index} icon={icon} text={text} />
            ))}
          </Stack>
        </Stack>
        <Typography
          sx={{
            color: "#464646",
            fontWeight: 600,
            fontSize: "18px",
            lineHeight: "22px",
          }}
        >
          Let’s dive in - explore smarter sales and marketing with INFUSE
          solutions!
        </Typography>
        <GradientButton
          loading={isLoading}
          disabled={isLoading}
          onClick={updateFirstLoginHandler}
        >
          Get Started Now
        </GradientButton>
      </Stack>
    </Stack>
  );
};

export default WelcomePage;
